import {
    SAAS_V1,
    BIDDING_DISABLED,
    LTL_MARKETPLACE,
    SHIPPER_BIDDING_V1,
    BROKER,
    ADVANCED_SCHEDULING,
    ACCOUNTING_V2,
    ONWARD_HANDLED_QUOTES,
    DRIVER_PAYMENTS,
    WMS,
    SHIPPER_RATE_SHOP as SHIPPER_RATE_SHOP_FEATURE,
    ASSET_HEAVY,
} from '@/constants/featureFlags';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import EmailIcon from '@material-ui/icons/Email';
import HelpIcon from '@material-ui/icons/Help';
import HomeIcon from '@material-ui/icons/Home';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import Build from '@material-ui/icons/Build';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import WarningIcon from '@material-ui/icons/Warning';
import NearMe from '@material-ui/icons/NearMe';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import SettingsRemoteIcon from '@material-ui/icons/SettingsRemote';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ScheduleIcon from '@material-ui/icons/Schedule';
import MapIcon from '@material-ui/icons/Map';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';

import QuestionAnswer from '@material-ui/icons/QuestionAnswer';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import ListAlt from '@material-ui/icons/ListAlt';
import LocalShipping from '@material-ui/icons/LocalShipping';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

import * as ROUTES from '../../constants/routes';
import { AttachMoney, GetAppOutlined, Map } from '@material-ui/icons';
import StoreOutlined from '@material-ui/icons/StoreOutlined';

const CONTACT = {
    label: 'Contact Us',
    path: '/contactus',
    Icon: EmailIcon,
    disableWhiteLabel: true,
};

const HOME = {
    label: 'Home',
    path: '/account',
    Icon: HomeIcon,
};

const IMPORT = {
    label: 'Import',
    path: '/import',
    Icon: GetAppOutlined,
    feature: [SAAS_V1, LTL_MARKETPLACE, BROKER],
    lockedTooltipText:
        'Create orders for submission to be externally handled via the Onward Marketplace, or internally handled via our OMS system',
};

const EXCEPTION_MANAGEMENT = {
    label: 'Exception Manager',
    path: ROUTES.EXCEPTIONS,
    Icon: WarningIcon,
};

const CLAIMS = {
    label: 'Claims',
    path: ROUTES.CLAIMS,
    Icon: AssignmentLateIcon,
};

const ACCOUNTING = {
    label: 'Accounting',
    path: ROUTES.ACCOUNTING_RECEIVABLES,
    Icon: AttachMoneyIcon,
    sublinks: [
        {
            label: 'Completed Orders',
            path: ROUTES.ACCOUNTING_ORDERS,
        },
        {
            label: 'Receivables',
            path: ROUTES.ACCOUNTING_RECEIVABLES,
        },
        {
            label: 'Payables',
            path: ROUTES.ACCOUNTING_PAYABLES,
        },
    ],
};

// Accounting for a corporate admin whose organization owns no SHIPPER accounts (a carrier-only
// org such as WPL). Receivables and Payables scope on carrier_invoices.organization_id, which is
// always the SHIPPER's org — so for this kind of org they are permanently empty and are dropped
// rather than shown blank. Invoices takes their place, aggregated across the org's own accounts
// (see Accounting/Invoices/store.js and hooks/useCarrierOrgAggregate.js).
//
// Completed Orders is kept as-is and is NOT aggregated: its isAdmin branch matches on shipper_id,
// so it is also empty at org level for a carrier-only org. Left unchanged deliberately — scoping
// it correctly meant altering isAdmin, which gates shipper-pricing visibility and edit rights as
// well as row selection, and that was judged too broad a change to carry here. Known follow-up;
// unchanged from current behaviour, so nothing regresses by leaving the link in place.
const CORPORATE_CARRIER_ACCOUNTING = {
    label: 'Accounting',
    path: ROUTES.ACCOUNTING_ORDERS,
    Icon: AttachMoneyIcon,
    sublinks: [
        {
            label: 'Completed Orders',
            path: ROUTES.ACCOUNTING_ORDERS,
        },
        {
            label: 'Invoices',
            path: ROUTES.ACCOUNTING_INVOICES,
        },
    ],
};

const CARRIER_SCHEDULING = {
    label: 'Scheduling',
    path: ROUTES.RESOURCES,
    Icon: CalendarTodayIcon,
    lockedTooltipText:
        'Assign regions to specific days of the week that you deliver to those areas. Send out notifications manually to customers and track who has responded and how.',
    sublinks: [
        {
            label: 'Notifications',
            path: ROUTES.NOTIFICATIONS,
            feature: [ADVANCED_SCHEDULING],
        },
        {
            label: 'Regions',
            path: ROUTES.SUBREGION,
        },
        {
            label: 'Resources',
            path: ROUTES.RESOURCES,
            feature: [SAAS_V1],
        },
    ],
};

const SHIPPER_SCHEDULING = {
    label: 'Scheduling',
    path: ROUTES.RESOURCES,
    Icon: CalendarTodayIcon,
    feature: SAAS_V1,
    lockedTooltipText:
        'Assign regions to specific days of the week that you deliver to those areas. Send out notifications manually to customers and track who has responded and how.',
    sublinks: [
        {
            label: 'Notifications',
            path: ROUTES.NOTIFICATIONS,
            feature: [ADVANCED_SCHEDULING],
        },
        {
            label: 'Regions',
            path: ROUTES.SUBREGION,
        },
        {
            label: 'Resources',
            path: ROUTES.RESOURCES,
            feature: [SAAS_V1],
        },
    ],
};

const WAREHOUSING = {
    label: 'Warehousing',
    path: ROUTES.WAREHOUSES,
    feature: WMS,
    hideWhenDisabled: true,
    Icon: WarehouseOutlinedIcon,
    lockedTooltipText:
        'Create each warehouse by adding racks and floor storage. Use these storage areas to store your manifests for easy storage, relocating, and picking.',
    sublinks: [
        {
            label: 'Inventory Management',
            path: ROUTES.INVENTORY,
        },
        {
            label: 'Warehouses',
            path: ROUTES.WAREHOUSES,
        },
        {
            label: 'Manifests',
            path: ROUTES.MANIFESTS,
        },
        {
            label: 'Cycle Count',
            path: ROUTES.CYCLE_COUNTS,
        },
    ],
};

const DISPATCH = (entity) => ({
    label: 'Dispatch',
    path: '/dispatch',
    Icon: NearMe,
    feature: SAAS_V1,
    hideWhenDisabled: true,
    sublinks: [
        {
            label: 'Unscheduled',
            path: '/dispatch/unassigned',
            lockedTooltipText: 'Create routes on an interactive map using your internal and marketplace orders.',
        },
        {
            label: 'Plan',
            path: '/dispatch/plan',
            lockedTooltipText: 'Create routes on an interactive map using your internal and marketplace orders.',
        },
    ],
});

const TRACKING = (entity) => ({
    label: 'Tracking',
    Icon: MapIcon,
    path: `/${entity}/tracking/all`,
});

const SHIPPER_OMS = {
    label: 'OMS',
    path: '/oms',
    Icon: ListAlt,
    sublinks: [
        {
            label: 'My Orders',
            path: '/shipper/oms/myorders',
        },
        {
            label: 'My Routes',
            path: '/shipper/oms/myroutes',
            feature: SAAS_V1,
            hideWhenDisabled: true,
        },
    ],
};

const SHIPPER_RATE_SHOP = {
    label: 'Delivery Profiles',
    path: '/shipper/integrations',
    // todo: migrate to ROUTES.RATE_SHOP
    Icon: ContentPasteGoIcon,
    feature: SHIPPER_RATE_SHOP_FEATURE,
    hideWhenDisabled: true,
};

const CARRIER_OMS = {
    label: 'OMS',
    path: '/oms',
    Icon: ListAlt,
    sublinks: [
        {
            label: 'My Orders',
            path: '/carrier/oms/myorders',
        },
        {
            label: 'My Routes',
            path: '/carrier/oms/myroutes',
        },
    ],
};

const CARRIER_ACCOUNTING = {
    label: 'Accounting',
    path: ROUTES.ACCOUNTING_ORDERS,
    Icon: AttachMoney,
    teammateRoles: ['admin', 'invoicing'],
    hideWhenDisabled: true,
    sublinks: [
        {
            label: 'Completed Orders',
            path: ROUTES.ACCOUNTING_ORDERS,
        },
        {
            label: 'Receivables',
            path: ROUTES.ACCOUNTING_RECEIVABLES,
            feature: ASSET_HEAVY,
            hideWhenDisabled: true,
        },
        {
            label: 'Payables',
            path: ROUTES.ACCOUNTING_PAYABLES,
            feature: ASSET_HEAVY,
            hideWhenDisabled: true,
        },
        {
            label: 'Invoices',
            path: ROUTES.ACCOUNTING_INVOICES,
            restrictionFeature: [ASSET_HEAVY],
            hideWhenDisabled: true,
        },
    ],
};

const SHIPPER_AUCTIONED_ORDERS = {
    label: 'Quote Summary',
    path: '/auction',
    Icon: SellOutlinedIcon,
    sublinks: [
        {
            label: 'Quote Summary',
            path: '/auction/listings',
        },
        {
            label: 'Quotes',
            path: '/auction/bids',
            feature: SHIPPER_BIDDING_V1,
        },
    ],
    restrictionFeature: [BIDDING_DISABLED, ONWARD_HANDLED_QUOTES],
    lockedTooltipText: 'Allows creating listings and submitting quotes for orders',
};

const CARRIER_AUCTIONED_ORDERS = {
    label: 'Quote Summary',
    path: '/auction',
    Icon: SellOutlinedIcon,
    onwardGreen: true,
    sublinks: [
        {
            label: 'Listings',
            path: '/auction/listings',
        },
        {
            label: 'Quotes',
            path: '/auction/bids',
        },
    ],
    restrictionFeature: [BIDDING_DISABLED],
    lockedTooltipText: 'Allows creating listings and submitting quotes for orders',
};

const CARRIER_LOADBOARD = {
    label: 'Load Board',
    path: '/carrier/loads',
    Icon: StoreOutlined,
    onwardGreen: true,
    sublinks: [
        {
            label: 'LTL',
            path: '/carrier/loads/ltl',
        },
        {
            label: 'FTL',
            path: '/carrier/loads/ftl',
        },
    ],
};

// Shared by CORPORATE_ADMIN and CORPORATE_ADMIN_CARRIER_ORG — everything above Accounting.
const CORPORATE_ADMIN_BASE = [
    HOME,
    {
        label: 'Accounts',
        path: ROUTES.CORPORATE_USERS,
        Icon: SupervisorAccountIcon,
    },
    {
        label: 'Delivery Profiles',
        path: ROUTES.AUTO_ASSIGN,
        Icon: Map,
        sublinks: [
            {
                label: 'Auto Assign',
                path: ROUTES.AUTO_ASSIGN,
            },
            // {
            //     label: 'Carriers',
            //     path: ROUTES.RATE_SHOP,
            // },
        ],
    },
    {
        label: 'Tariffs',
        path: ROUTES.ADMIN_TARIFFS,
        Icon: LocalAtmIcon,
        sublinks: [
            {
                label: 'Tariffs',
                path: ROUTES.ADMIN_TARIFFS,
            },
            {
                label: 'Tags',
                path: ROUTES.CORPORATE_TAGS,
            },
        ],
    },
    IMPORT,
    SHIPPER_OMS,
    CLAIMS,
];

export const LINKS = {
    ADMIN: [
        {
            label: 'Super Admin',
            path: '/admin/users',
            Icon: SupervisorAccountIcon,
        },
        {
            label: 'Load Board',
            path: ROUTES.ADMIN_ORDERS,
            Icon: StoreOutlined,
        },
        {
            label: 'FTL Board',
            path: '/admin/ftl',
            Icon: LocalShipping,
        },
        {
            label: 'Control Tower',
            path: '/admin/tracking',
            Icon: SettingsRemoteIcon,
            sublinks: [
                {
                    label: 'FTL',
                    path: '/admin/tracking/FTL',
                },
            ],
        },
        {
            label: 'Delivery Profiles',
            path: ROUTES.AUTO_ASSIGN,
            Icon: Map,
            sublinks: [
                {
                    label: 'Auto Assign',
                    path: ROUTES.AUTO_ASSIGN,
                },
                {
                    label: 'Carriers',
                    path: ROUTES.RATE_SHOP,
                },
            ],
        },
        {
            label: 'Matching Tool',
            path: ROUTES.ADMIN_MATCHING_TOOL,
            Icon: LocalShipping,
        },
        {
            label: 'Quote Summary',
            path: ROUTES.AUCTIONED_ORDERS_LISTINGS,
            Icon: SellOutlinedIcon,
        },
        ACCOUNTING,
        {
            label: 'Tariffs',
            path: ROUTES.ADMIN_TARIFFS,
            Icon: LocalAtmIcon,
            sublinks: [
                {
                    label: 'Tariffs',
                    path: ROUTES.ADMIN_TARIFFS,
                },
                {
                    label: 'Tags',
                    path: ROUTES.ADMIN_TAGS,
                },
            ],
        },
        {
            label: 'Pricing',
            path: ROUTES.ADMIN_PRICING,
            Icon: ContentPasteGoIcon,
        },
        {
            label: 'Exception Manager',
            path: ROUTES.ADMIN_EXCEPTIONS,
            Icon: WarningIcon,
        },
        CLAIMS,
        {
            label: 'Notes',
            path: ROUTES.ADMIN_NOTES,
            Icon: DescriptionOutlinedIcon,
        },
        {
            label: 'Reports',
            path: ROUTES.ADMIN_REPORTS,
            Icon: AssessmentIcon,
        },
        {
            label: 'Integrations',
            path: ROUTES.ADMIN_INTEGRATIONS,
            Icon: CloudDownloadIcon,
        },
        {
            label: 'Automations',
            path: ROUTES.ADMIN_AUTOMATIONS,
            Icon: ScheduleIcon,
        },
        {
            label: 'Communications',
            path: '/admin/communications',
            Icon: QuestionAnswer,
        },
    ],
    SHIPPER: [
        HOME,
        IMPORT,
        WAREHOUSING,
        // SHIPPER_SCHEDULING,
        SHIPPER_OMS,
        DISPATCH('shipper'),
        TRACKING('shipper'),
        SHIPPER_AUCTIONED_ORDERS,
        EXCEPTION_MANAGEMENT,
        CLAIMS,
        SHIPPER_RATE_SHOP,
    ],
    CARRIER: [
        CARRIER_LOADBOARD,
        CARRIER_AUCTIONED_ORDERS,
        HOME,
        IMPORT,
        WAREHOUSING,
        CARRIER_SCHEDULING,
        CARRIER_OMS,
        DISPATCH('carrier'),
        TRACKING('carrier'),
        EXCEPTION_MANAGEMENT,
        CLAIMS,
        CARRIER_ACCOUNTING,
    ],
    CORPORATE_ADMIN: [...CORPORATE_ADMIN_BASE, ACCOUNTING],
    // Same menu, but Receivables/Payables swapped for Invoices. Selected in Navigation/index.js
    // when the admin's organization owns no SHIPPER accounts.
    CORPORATE_ADMIN_CARRIER_ORG: [...CORPORATE_ADMIN_BASE, CORPORATE_CARRIER_ACCOUNTING],
    SHIPPER_DRIVER_TEAMMATE: [HOME],
    CARRIER_DRIVER_TEAMMATE: [HOME],
    FOOTER: [CONTACT],
};
